import { useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useResultsInvalidation, useUpdateLoadInfo } from '@editorHooks'
import { getWindLoadSettings } from '@queries'
import { computeWindloadResults, resetWindLoad, saveWindLoadSettings } from '@mutations'
import { formValuesToWindLoadSettings } from '../utils/windLoadFormUtils'
import useInvalidateWindLoadResults from './useWindLoadsInvalidation'

const useWindloadsMutations = () => {
  const { enqueueSnackbar } = useSnackbar()

  const invalidateResults = useResultsInvalidation()
  const invalidateWindLoadResults = useInvalidateWindLoadResults()
  const queryClient = useQueryClient()
  const updateProjectLoadInfo = useUpdateLoadInfo()

  const { projectId }: { projectId?: string } = useParams()

  const { mutate: compute, isLoading: isComputingWindLoads } = useMutation(
    () => computeWindloadResults.request(projectId),
    {
      onSuccess: () => {
        invalidateWindLoadResults(projectId as string)
        updateProjectLoadInfo()
      },
      onError: () => {
        enqueueSnackbar('Fehler beim Neuberechnen der Windlastkonfiguration', { variant: 'error' })
      },
    },
  )

  const {
    mutate: reset,
    mutateAsync: resetAsync,
    isLoading: isResettingWindLoad,
  } = useMutation(() => resetWindLoad.request(projectId), {
    onSuccess: () => {
      invalidateWindLoadResults(projectId as string)
      queryClient.invalidateQueries(getWindLoadSettings.getKey(projectId))
      updateProjectLoadInfo()
      enqueueSnackbar('Windlasten zurückgesetzt', { variant: 'success' })
    },
    onError: () => {
      enqueueSnackbar('Fehler beim Zurücksetzen der Windlasten', { variant: 'error' })
    },
  })

  const { mutate, isLoading: isSavingWindLoads } = useMutation(
    async (data: WindLoadsFormValues) => {
      await saveWindLoadSettings.request(projectId, formValuesToWindLoadSettings(data))
    },
    {
      onSuccess: () => {
        invalidateResults(projectId as string)
        queryClient.invalidateQueries(getWindLoadSettings.getKey(projectId))
        invalidateWindLoadResults(projectId as string)
        enqueueSnackbar('Windlasten erfolgreich gespeichert', { variant: 'success' })
        compute()
      },
      onError: () => {
        enqueueSnackbar('Fehler beim Speichern der Windlasten', { variant: 'error' })
      },
    },
  )

  return {
    mutate,
    isSaving: isSavingWindLoads,
    isComputingWindLoads,
    resetWindLoad: reset,
    resetWindLoadAsync: resetAsync,
    isResettingWindLoad,
  }
}

export default useWindloadsMutations
